import { useStore } from 'vuex';
import { computed, nextTick, Ref } from 'vue';
import useNotes from '@/utilities/composables/useNotes';
import useTranslate from '@/utilities/composables/useTranslate';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useNoteContent from '@/utilities/composables/useNoteContent';
import EventBus from '@/utilities/eventBus';
import constants from '../constants';
import { useRouter, useRoute } from 'vue-router';

export default function useNoteOptionsDropdown(noteId: Ref<any>) {
  const store = useStore();
  const { deleteNote } = useNotes?.() || {};
  const { noteTitle } = useNoteContent(noteId) || {};
  const { translate } = useTranslate();
  const { iconPath } = useCustomIcon();
  const router = useRouter();
  const route = useRoute();
  const isInboxNoteReport = computed(() => route.name === constants.routeNames.inbox);

  const currentEditor = computed(() => store.getters.currentEditor);

  const getNoteForClipboard = (content: string = '') => {
    const title = noteTitle?.value ? `${noteTitle.value}\n` : '';
    return `${title}${content.replace(/(\n)\1{3,4}/g, '\n')}`;
  };

  const noteOptionClick = async (option: any, hide?: Function) => {
    hide?.();

    switch (option.id) {
      case 'favouriteNote':
        break;
      case 'archiveNote':
        break;
      case 'deleteNote': {
        const toBeDeletedId = structuredClone(noteId.value);
        EventBus.$emit('deleteNote', toBeDeletedId);
        nextTick(() => {
          deleteNote?.(toBeDeletedId);
        });
        break;
      }
      case 'copyNoteToClipboard': {
        const html = getNoteForClipboard(currentEditor.value.getHTML());
        const text = getNoteForClipboard(currentEditor.value.getText());

        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);

        store.dispatch('addUiNotification', {
          message: translate('noteCopiedToClipboard'),
          duration: 4000,
          icon: 'note',
        });
        break;
      }
      case 'notesSettings': {
        router.push({ name: constants.routeNames.notesSettings });
      }
    }
  };

  const noteOptions = computed(() => {
    return [
      {
        id: 'copyNoteToClipboard',
        label: translate('copyNoteToClipboard'),
        customIconName: iconPath('note'),
        separator: isInboxNoteReport.value ? true : false,
      },
      ...(isInboxNoteReport.value
        ? []
        : [
            {
              id: 'deleteNote',
              label: translate('deleteNote'),
              customIconName: iconPath('trash'),
              deleteOrCancelType: true,
              separator: true,
            },
          ]),
      {
        id: 'notesSettings',
        label: translate('notesSettings'),
        customIconName: iconPath('settings'),
      },
    ];
  });

  return {
    noteOptions,
    noteOptionClick,
  };
}
